import { Ev, type EventBuffer, Grade, GradeKind, LinkEnd, S, type SimState, type World } from "@lache/sim";

export const GYM_LESSONS = [
  "run",
  "jump",
  "slide",
  "lache",
  "block",
  "roll",
  "pump",
  "vault",
  "mount",
  "hop",
  "long-lache",
  "balance",
  "ladder",
  "mantle",
  "high-mantle",
] as const;
export type GymLessonId = (typeof GYM_LESSONS)[number];
export type GymTutorialAction =
  | "run"
  | "stop"
  | "jump"
  | "land"
  | "slide"
  | "catch"
  | "release"
  | "link"
  | "block"
  | "roll"
  | "pump"
  | "vault"
  | "mount"
  | "hop"
  | "balance"
  | "ladder-duck"
  | "ladder-release"
  | "ledge-grab"
  | "mantle"
  | "done";
export interface GymTutorialView {
  readonly lessonId: GymLessonId;
  readonly title: string;
  readonly action: GymTutorialAction;
  readonly feedback: string | null;
  readonly completed: boolean;
  readonly completedCount: number;
  readonly total: number;
}

const TITLES: Record<GymLessonId, string> = {
  run: "Run & stop",
  jump: "Jump & land",
  slide: "Slide",
  lache: "Linked laches",
  block: "Jump onto a block",
  roll: "Landing roll",
  pump: "Build your swing",
  vault: "Vault",
  mount: "Mount a wide box",
  hop: "Low hurdles",
  "long-lache": "Long laches",
  balance: "Precision jumps",
  ladder: "Salmon ladder",
  mantle: "Wall mantle",
  "high-mantle": "High wall",
};
const ACTIONS: Record<GymLessonId, GymTutorialAction> = {
  run: "run",
  jump: "jump",
  slide: "slide",
  lache: "catch",
  block: "block",
  roll: "roll",
  pump: "catch",
  vault: "vault",
  mount: "mount",
  hop: "hop",
  "long-lache": "catch",
  balance: "balance",
  ladder: "catch",
  mantle: "ledge-grab",
  "high-mantle": "ledge-grab",
};
const BODY_MARGIN = 0.3;
const FEEDBACK_TICKS = 180;

/**
 * Local, untimed Gym coaching. Consume the event buffer immediately after every simulation step.
 * No inputs, simulation state, browser state or saved data are changed. Progress lasts for this
 * instance; resetting an attempt leaves accomplishments intact. Fixed arrays bound all tracking.
 */
export class GymTutorial {
  private readonly world: World;
  private readonly block: number;
  private readonly wideBox: number;
  private readonly lowBox: number;
  private readonly ladderLanding: number;
  private readonly lowWall: number;
  private readonly highWall: number;
  private readonly balancePlatforms: number[];
  private readonly firstBars: number[];
  private readonly longBars: number[];
  private readonly highBar: number;
  private readonly plants: Uint8Array;
  private readonly plantDirs: Int8Array;
  private readonly slides: Uint8Array;
  private readonly slideDirs: Int8Array;
  private completedMask = 0;
  private completedCount = 0;
  private current: GymLessonId = "run";
  private cached: GymTutorialView | null = null;
  private clock = 0;
  private lastTick = -1;
  private previousX = Number.NaN;
  private previousGrounded = false;
  private runStarted = false;
  private runDistance = 0;
  private jumped = false;
  private blockJump = false;
  private rolling = false;
  private wasTumbling = false;
  private firstCaught = 0;
  private firstLinks = 0;
  private longCaught = 0;
  private longLinks = 0;
  private balanceLanded = 0;
  private ladderRungs = 0;
  private wallGrabbed = 0;
  private wallMantled = 0;
  private pumpCaught = false;
  private pumpStartAngle = 0;
  private pumpReady = false;
  private pumpReleased = false;
  private feedback: string | null = null;
  private feedbackUntil = 0;
  private feedbackLesson: GymLessonId = "run";
  private feedbackSuccess = false;

  constructor(world: World) {
    this.world = world;
    // Resolve authored obstacles once. Entity insertion cannot silently retarget a lesson.
    const at = (x: number) => {
      for (let i = 0; i < world.solidCount; i++) if (world.solidX0[i] === x) return i;
      return -1;
    };
    this.block = at(50);
    this.wideBox = at(87.5);
    this.lowBox = at(93.5);
    this.ladderLanding = at(190.5);
    this.lowWall = at(210);
    this.highWall = at(218);
    this.balancePlatforms = [at(154.2), at(157.9), at(161.1), at(164)];
    this.firstBars = [];
    this.longBars = [];
    let highBar = -1;
    for (let i = 0; i < world.barCount; i++) {
      const x = world.barX[i]!;
      if (world.barLadder[i] !== -1) continue;
      if (x >= 30 && x <= 42) this.firstBars.push(i);
      else if (x >= 116 && x <= 138) this.longBars.push(i);
      else if (x === 59.5) highBar = i;
    }
    this.highBar = highBar;
    this.plants = new Uint8Array(world.solidCount);
    this.plantDirs = new Int8Array(world.solidCount);
    this.slides = new Uint8Array(world.solidCount);
    this.slideDirs = new Int8Array(world.solidCount);
  }

  isCompleted(id: GymLessonId): boolean {
    return (this.completedMask & (1 << GYM_LESSONS.indexOf(id))) !== 0;
  }

  resetAttempt(): void {
    this.clearAttempt();
    this.feedback = null;
    this.feedbackUntil = 0;
    this.lastTick = -1;
    this.cached = null;
  }

  view(): GymTutorialView | null {
    return this.cached;
  }

  update(s: Readonly<SimState>, events: EventBuffer): void {
    if (this.world.name !== "gym" || s.tick === this.lastTick) return;
    if (s.tick < this.lastTick) this.resetAttempt();
    this.lastTick = s.tick;
    this.clock++;
    this.current = this.context(s);
    let failed = false;
    let landed = false;
    for (let i = 0; i < events.count; i++) {
      const type = events.type[i]!;
      const a = events.a[i]!;
      const b = events.b[i]!;
      const c = events.c[i]!;
      if (type === Ev.Failed) {
        this.clearAttempt();
        this.say(this.current, "Try again — you can retry this section.");
        failed = true;
      } else if (type === Ev.Respawned) {
        this.clearAttempt();
      } else if (type === Ev.RunStart && s.x < 28) {
        this.runStarted = true;
      } else if (type === Ev.Jumped) {
        if (s.x < 28) this.jumped = true;
        if (s.x >= 44 && s.x <= 56) this.blockJump = true;
      } else if (type === Ev.Landed) {
        landed = true;
      } else if (type === Ev.Gripped) {
        const first = this.firstBars.indexOf(a);
        const long = this.longBars.indexOf(a);
        if (first >= 0) this.firstCaught |= 1 << first;
        if (long >= 0) this.longCaught |= 1 << long;
        if (this.world.barLadder[a] === 0) this.ladderRungs = 0;
        if (a === this.highBar) {
          this.pumpCaught = true;
          this.pumpStartAngle = Math.abs(s.theta);
          this.pumpReady = false;
          this.pumpReleased = false;
        }
      } else if (type === Ev.Link) {
        // Ladder chains also emit Link; only the ordinary bars belong to these lessons.
        if (this.firstBars.includes(b)) this.firstLinks++;
        if (this.longBars.includes(b)) this.longLinks++;
      } else if (type === Ev.LinkBroken && (b === LinkEnd.Backswing || b === LinkEnd.Hang)) {
        if (this.current === "lache" || this.current === "long-lache")
          this.say(this.current, "Try releasing on the forward swing.");
        this.firstLinks = 0;
        this.longLinks = 0;
        this.firstCaught = this.firstBars.includes(s.barId) ? 1 << this.firstBars.indexOf(s.barId) : 0;
        this.longCaught = this.longBars.includes(s.barId) ? 1 << this.longBars.indexOf(s.barId) : 0;
      } else if (type === Ev.Released && this.pumpCaught && s.lastBarId === this.highBar) {
        this.pumpReleased = this.pumpReady;
      } else if (type === Ev.Pump && s.barId === this.highBar && a >= Grade.Good) {
        this.pumpReady = true;
      } else if (type === Ev.Planted && a >= 0 && a < this.plants.length) {
        this.plants[a] = 1;
        this.plantDirs[a] = s.facing;
      } else if (type === Ev.Hit && a >= 0 && a < this.plants.length) {
        this.plants[a] = 0;
        this.slides[a] = 2;
        this.say(this.current, "Try again — duck before the obstacle.");
      } else if (type === Ev.Mounted) {
        if (a === this.wideBox && b === 0 && this.plants[a]) this.complete("mount");
        const bit = a === this.lowWall ? 1 : a === this.highWall ? 2 : 0;
        if (b === 1 && bit && this.wallGrabbed & bit && this.wallMantled & bit)
          this.complete(bit === 1 ? "mantle" : "high-mantle");
      } else if (type === Ev.Rolled && a >= Grade.Ok) {
        this.rolling = true;
      } else if (type === Ev.HardLanding && !this.isCompleted("roll") && s.x >= 44 && s.x < 74) {
        this.say("roll", "Try again — tuck and lean before landing.");
      } else if (type === Ev.Popped && a === 0 && b > 0 && b < this.world.ladderRungs[0]!) {
        this.ladderRungs |= 1 << b;
        this.say("ladder", "Perfect — rung reached.");
      } else if (type === Ev.LedgeGrabbed) {
        const bit = a === this.lowWall ? 1 : a === this.highWall ? 2 : 0;
        this.wallGrabbed |= bit;
        this.wallMantled &= ~bit;
      } else if (type === Ev.Mantled) {
        this.wallMantled |= a === this.lowWall ? 1 : a === this.highWall ? 2 : 0;
      } else if (type === Ev.LedgeDropped) {
        const bit = a === this.lowWall ? 1 : a === this.highWall ? 2 : 0;
        this.wallGrabbed &= ~bit;
        this.wallMantled &= ~bit;
      } else if (type === Ev.Graded && a === GradeKind.Pump && s.ladderId >= 0 && b < Grade.Perfect) {
        this.say(
          "ladder",
          c < 0 ? "Too early — duck nearer the bottom." : "Too late — duck before the bottom.",
        );
      }
    }
    if (!failed && s.state !== S.Fail) this.observe(s, landed);
    this.previousX = s.x;
    this.previousGrounded = s.grounded === 1;
    this.wasTumbling = s.tumbling === 1;
    this.refresh(s);
  }

  private clearAttempt(): void {
    this.plants.fill(0);
    this.slides.fill(0);
    this.previousX = Number.NaN;
    this.previousGrounded = false;
    this.runStarted = false;
    this.runDistance = 0;
    this.jumped = false;
    this.blockJump = false;
    this.rolling = false;
    this.wasTumbling = false;
    this.firstCaught = 0;
    this.firstLinks = 0;
    this.longCaught = 0;
    this.longLinks = 0;
    this.balanceLanded = 0;
    this.ladderRungs = 0;
    this.wallGrabbed = 0;
    this.wallMantled = 0;
    this.pumpCaught = false;
    this.pumpReady = false;
    this.pumpReleased = false;
  }

  private on(s: Readonly<SimState>, solid: number): boolean {
    return (
      solid >= 0 &&
      s.grounded === 1 &&
      s.x > this.world.solidX0[solid]! &&
      s.x < this.world.solidX1[solid]! &&
      Math.abs(s.y - this.world.solidY1[solid]!) < 0.05
    );
  }

  private observe(s: Readonly<SimState>, landed: boolean): void {
    const world = this.world;
    if (this.runStarted && s.x < 28 && s.grounded && this.previousGrounded) {
      const distance = Math.abs(s.x - this.previousX);
      if (distance < 1 && Math.abs(s.vx) > 1) this.runDistance += distance;
      if (this.runDistance >= 2 && Math.abs(s.vx) < 0.4) this.complete("run");
    }
    if (landed && this.jumped && s.x < 28) {
      this.complete("jump");
      this.jumped = false;
    }
    if (this.blockJump && this.on(s, this.block)) this.complete("block");
    if (this.rolling && !s.tumbling) {
      if (s.grounded && this.wasTumbling) this.complete("roll");
      this.rolling = false;
    }
    if (
      this.pumpCaught &&
      s.barId === this.highBar &&
      (Math.abs(s.barLean) > 0.08 || s.prevTuck === 1) &&
      Math.abs(s.theta) >= this.pumpStartAngle + 0.3
    )
      this.pumpReady = true;
    if (landed && this.pumpReleased) this.complete("pump");
    if (
      this.firstCaught === (1 << this.firstBars.length) - 1 &&
      this.firstLinks >= 2 &&
      s.grounded &&
      s.x > 42 &&
      s.x < 50 &&
      Math.abs(s.y) < 0.05
    )
      this.complete("lache");
    if (
      this.longCaught === (1 << this.longBars.length) - 1 &&
      this.longLinks >= 5 &&
      s.grounded &&
      s.x > 138 &&
      s.x < 152 &&
      Math.abs(s.y) < 0.05
    )
      this.complete("long-lache");
    if (landed) {
      // Touching down ends a chain. Its completed success was checked immediately above.
      this.firstCaught = 0;
      this.firstLinks = 0;
      this.longCaught = 0;
      this.longLinks = 0;
    }
    if (landed) {
      for (let i = 0; i < this.balancePlatforms.length; i++)
        if (this.on(s, this.balancePlatforms[i]!)) this.balanceLanded |= 1 << i;
    }
    if (this.balanceLanded === 15 && s.grounded && s.x > 166.7 && s.x < 184 && Math.abs(s.y) < 0.05)
      this.complete("balance");
    if (this.ladderRungs === (1 << world.ladderRungs[0]!) - 2 && this.on(s, this.ladderLanding))
      this.complete("ladder");
    if (s.plantBox === this.lowBox && s.planted !== 0 && s.plantHands === 0) {
      this.plants[this.lowBox] = 1;
      this.plantDirs[this.lowBox] = s.facing;
    }
    for (let i = 0; i < world.solidCount; i++) {
      const left = world.solidX0[i]!;
      const right = world.solidX1[i]!;
      // Only Gym's two teaching pipes, not an arbitrary crouch in the open.
      if (left === 12 || left === 98.5) {
        if (s.x < left - 1 || s.x > right + 1) {
          if (this.slides[i] === 2) this.slides[i] = 0;
        }
        if (
          this.slides[i] === 0 &&
          s.grounded &&
          s.x > left &&
          s.x < right &&
          s.y < world.solidY0[i]! &&
          s.tuck > 0.2
        ) {
          this.slides[i] = 1;
          this.slideDirs[i] = s.vx < 0 ? -1 : 1;
        }
        if (
          this.slides[i] === 1 &&
          s.grounded &&
          (this.slideDirs[i]! > 0 ? s.x > right + BODY_MARGIN : s.x < left - BODY_MARGIN)
        ) {
          this.complete("slide");
          this.slides[i] = 0;
        }
      }
      if (
        this.plants[i] &&
        s.grounded &&
        (this.plantDirs[i]! > 0 ? s.x > right + BODY_MARGIN : s.x < left - BODY_MARGIN)
      ) {
        if (i === this.lowBox) this.complete("hop");
        else if (left === 80 || left === 104.5) this.complete("vault");
        this.plants[i] = 0;
      }
    }
  }

  private context(s: Readonly<SimState>): GymLessonId {
    // Keep an airborne attempt's cue through its landing; section jumps explicitly reset it.
    if (s.barId === this.highBar || (this.pumpReleased && !s.grounded)) return "pump";
    if (s.ladderId >= 0 || s.popping || (this.ladderRungs && s.x >= 178 && s.x < 198)) return "ladder";
    if (s.ledgeSolid === this.lowWall) return "mantle";
    if (s.ledgeSolid === this.highWall) return "high-mantle";
    if (this.rolling && s.x >= 44 && s.x < 74) return "roll";
    const x = s.x;
    if (x < 8) return this.isCompleted("run") ? "jump" : "run";
    if (x < 16) return "slide";
    if (x < 23) return "jump";
    if (x < 44) return "lache";
    if (x < 56) return this.isCompleted("block") ? "roll" : "block";
    if (x < 74) return "pump";
    if (x < 85) return "vault";
    if (x < 92) return "mount";
    if (x < 96) return "hop";
    if (x < 102) return "slide";
    if (x < 109) return "vault";
    if (x < 146) return "long-lache";
    if (x < 178) return "balance";
    if (x < 204) return "ladder";
    if (x < 216) return "mantle";
    return "high-mantle";
  }

  private action(id: GymLessonId, s: Readonly<SimState>): GymTutorialAction {
    if (this.isCompleted(id)) return "done";
    if (id === "run" && this.runDistance >= 2) return "stop";
    if (id === "jump" && this.jumped && !s.grounded) return "land";
    if (id === "lache" || id === "long-lache") {
      if (s.barId >= 0) return "release";
      const caught = id === "lache" ? this.firstCaught : this.longCaught;
      const count = id === "lache" ? this.firstBars.length : this.longBars.length;
      if (caught === (1 << count) - 1 && !s.grounded) return "land";
      if (id === "lache" ? this.firstCaught !== 0 : this.longCaught !== 0) return "link";
    }
    if (id === "pump" && s.barId === this.highBar) return this.pumpReady ? "release" : "pump";
    if (id === "pump" && this.pumpReleased) return "land";
    if (id === "ladder" && s.ladderId >= 0)
      return s.ladderRung === this.world.ladderRungs[s.ladderId]! - 1 ? "ladder-release" : "ladder-duck";
    if (id === "ladder" && this.ladderRungs && !s.grounded) return "land";
    if ((id === "mantle" || id === "high-mantle") && s.ledgeSolid >= 0) return "mantle";
    return ACTIONS[id];
  }

  private complete(id: GymLessonId): void {
    if (this.isCompleted(id)) return;
    this.completedMask |= 1 << GYM_LESSONS.indexOf(id);
    this.completedCount++;
    this.say(id, TITLES[id] + " complete.", true);
  }

  private say(id: GymLessonId, text: string, success = false): void {
    if (this.feedback === text && this.feedbackLesson === id && this.clock < this.feedbackUntil) return;
    this.feedback = text;
    this.feedbackLesson = id;
    this.feedbackSuccess = success;
    this.feedbackUntil = this.clock + FEEDBACK_TICKS;
  }

  private refresh(s: Readonly<SimState>): void {
    const id = this.context(s);
    // A short success can accompany the next obstacle, but never hides its current instruction.
    const feedback =
      this.clock < this.feedbackUntil && (this.feedbackSuccess || this.feedbackLesson === id)
        ? this.feedback
        : null;
    const action = this.action(id, s);
    const old = this.cached;
    const completed = this.isCompleted(id);
    if (
      old?.lessonId === id &&
      old.action === action &&
      old.feedback === feedback &&
      old.completed === completed &&
      old.completedCount === this.completedCount
    )
      return;
    this.cached = {
      lessonId: id,
      title: TITLES[id],
      action,
      feedback,
      completed,
      completedCount: this.completedCount,
      total: GYM_LESSONS.length,
    };
  }
}
